import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Image, Button, message, Select, InputNumber, Switch, Typography, Slider, Row, Col } from 'antd';
import { PictureOutlined, PlayCircleFilled, LoadingOutlined, ExpandAltOutlined, DeleteOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import { useGeneration } from '../../../context/GenerationContext';

const { Text } = Typography;

const TopazUpscaleNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Parameters with defaults
    const upscaleFactor = data.upscaleFactor || 2;
    const model = data.model || 'Standard V2';
    const outputFormat = data.outputFormat || 'jpeg';
    const faceEnhancement = data.faceEnhancement !== undefined ? data.faceEnhancement : true;
    const faceEnhancementStrength = data.faceEnhancementStrength || 0.8;

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        // Persist to DB
        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const appEdges = getEdges();
            const appNodes = getNodes();

            // Find connected image source (Left Handle)
            const sourceEdge = appEdges.find(
                (e) => e.target === id && e.targetHandle === 'left'
            );

            if (!sourceEdge) {
                message.warning('Please connect an image to the left handle.');
                setGenerating(false);
                return;
            }

            const sourceNode = appNodes.find((n) => n.id === sourceEdge.source);
            if (!sourceNode || !sourceNode.data || !sourceNode.data.src) {
                message.warning('Connected node has no image.');
                setGenerating(false);
                return;
            }

            const imageUrl = sourceNode.data.src;

            const payload = {
                image_url: imageUrl,
                model,
                upscale_factor: upscaleFactor,
                output_format: outputFormat,
                face_enhancement: faceEnhancement,
                face_enhancement_strength: faceEnhancementStrength,
                canvas_id: canvasId,
                node_id: id
            };

            const response = await api.post('/fal/topaz/upscale', payload);
            const { request_id } = response.data;
            const modelId = 'fal-ai/topaz/upscale/image';

            // setGenerating(false); // REMOVED

            trackGeneration(request_id, modelId, {
                prompt: 'Topaz Upscale',
                type: 'Image',
                canvas_id: canvasId,
                node_id: id,
                promptProp: `Upscale ${upscaleFactor}x (${model})`
            }, {
                onComplete: (result) => {
                    if (result && result.image && result.image.url) {
                        const finalUrl = result.image.url;

                        // Update node data locally
                        setNodes((nds) => nds.map((node) => {
                            if (node.id === id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        src: finalUrl
                                    }
                                };
                            }
                            return node;
                        }));

                        // Persist to DB
                        if (data.onSave) {
                            data.onSave(id, { src: finalUrl });
                        }

                        message.success('Upscale complete!');
                    } else {
                        message.error('Failed to upscale image.');
                    }
                    setGenerating(false); // Stop glow
                },
                onFail: (err) => {
                    console.error("Upscale failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Upscale error:', error);
            message.error('Upscale failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false); // Stop glow if submission fails
        }
    };

    const clearImage = (e) => {
        e.stopPropagation();
        updateData('src', null);
    };

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}>
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={400}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Topaz Upscale'}
                iconType={data.iconType || 'picture'}
                color={data.nodeColor || '#13c2c2'}
                onTitleChange={onTitleChange}
            />

            {/* Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                id="left"
                style={getHandleStyle(TYPE_COLORS.image, 'left')}
            >
                <PictureOutlined style={{ color: '#fff', fontSize: 72, pointerEvents: 'none' }} />
            </Handle>

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={getHandleStyle(TYPE_COLORS.image, 'right')}
            >
                <PictureOutlined style={{ color: '#fff', fontSize: 72, pointerEvents: 'none' }} />
            </Handle>

            {/* Play Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 72 }} /> : <PlayCircleFilled style={{ fontSize: 72 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -100,
                    bottom: 0,
                    width: 120,
                    height: 120,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '12px solid #161b22',
                    background: '#722ed1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #722ed1' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: 8,
                    background: '#161b22',
                    transition: 'all 0.3s ease'
                }}
            >
                {/* Image Display Area */}
                <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 150 }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.src ? (
                        <div
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                            onMouseEnter={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 0}
                        >
                            <Image
                                src={data.src}
                                alt="upscaled"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                preview={{ visible: previewVisible, onVisibleChange: (vis) => setPreviewVisible(vis) }}
                            />
                            <div
                                className="nodrag delete-btn"
                                onClick={clearImage}
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 24,
                                    height: 24,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    zIndex: 10
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                            </div>
                            <style>{`
                                .preview-overlay:hover ~ .delete-btn,
                                .delete-btn:hover {
                                    opacity: 1 !important;
                                }
                            `}</style>
                            <div
                                className="preview-overlay nodrag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewVisible(true);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer'
                                }}
                            >
                                <ExpandAltOutlined style={{ fontSize: 24, color: '#fff', dropShadow: '0 2px 4px rgba(0,0,0,0.5)' }} />
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#21262d',
                            color: '#8b949e',
                            flexDirection: 'column'
                        }}>
                            <PictureOutlined style={{ fontSize: 32 }} />
                            <span style={{ fontSize: 12, marginTop: 8 }}>Output</span>
                        </div>
                    )}
                </div>

                {/* Controls Area */}
                <div style={{
                    padding: '12px',
                    background: '#161b22',
                    borderTop: '1px solid #30363d',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                }}>
                    <div className="nodrag">
                        <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Model</Text>
                        <Select
                            value={model}
                            onChange={(v) => updateData('model', v)}
                            options={[
                                { value: 'Standard V2', label: 'Standard V2' },
                                { value: 'Low Resolution V2', label: 'Low Resolution V2' },
                                { value: 'CGI', label: 'CGI' },
                                { value: 'High Fidelity V2', label: 'High Fidelity V2' },
                                { value: 'Text Refine', label: 'Text Refine' },
                                { value: 'Recovery', label: 'Recovery' },
                                { value: 'Redefine', label: 'Redefine' },
                                { value: 'Recovery V2', label: 'Recovery V2' }
                            ]}
                            style={{ width: '100%' }}
                            className="pill-select"
                            dropdownMatchSelectWidth={false}
                        />
                    </div>

                    <div className="nodrag" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: '48%' }}>
                            <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Upscale: {upscaleFactor}x</Text>
                            <Slider
                                min={1}
                                max={4}
                                step={0.1}
                                value={upscaleFactor}
                                onChange={(v) => updateData('upscaleFactor', v)}
                            />
                        </div>
                        <div style={{ width: '48%' }}>
                            <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Format</Text>
                            <Select
                                value={outputFormat}
                                onChange={(v) => updateData('outputFormat', v)}
                                options={[
                                    { value: 'jpeg', label: 'JPEG' },
                                    { value: 'png', label: 'PNG' }
                                ]}
                                style={{ width: '100%' }}
                                className="pill-select"
                            />
                        </div>
                    </div>

                    <div className="nodrag" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0d1117', padding: '6px 8px', borderRadius: 6 }}>
                        <Text style={{ color: '#c9d1d9', fontSize: 12 }}>Face Enhancement</Text>
                        <Switch
                            size="small"
                            checked={faceEnhancement}
                            onChange={(checked) => updateData('faceEnhancement', checked)}
                        />
                    </div>

                    {faceEnhancement && (
                        <div className="nodrag">
                            <Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>Strength: {faceEnhancementStrength}</Text>
                            <Slider
                                min={0}
                                max={1}
                                step={0.1}
                                value={faceEnhancementStrength}
                                onChange={(v) => updateData('faceEnhancementStrength', v)}
                            />
                        </div>
                    )}

                </div>
            </Card>
            <style>{`
                .pill-select .ant-select-selector {
                    background: #30363d !important;
                    border-radius: 6px !important;
                    color: #fff !important;
                    height: 32px !important;
                    display: flex !important;
                    align-items: center !important;
                    font-size: 13px !important;
                    border: none !important;
                }
                .pill-select .ant-select-arrow {
                    color: #8b949e;
                }
            `}</style>
        </div>
    );
};

export default memo(TopazUpscaleNode);
